import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Dialog } from '../../../components/ui/Dialog';
import { SelectInput } from '../../../components/form/inputs/SelectInput';
import { useDebounce } from '../../../hooks/useDebounce.js';
import { useGetSpidersQuery } from '../../../redux/services/parser/parserApi';
import {
  useCreateImportRuleMutation,
  useGetImportRulePreviewCountQuery,
  useGetRecordsQuery,
  useUpdateImportRuleMutation,
} from '../api/parserImportApi';
import { RadiusControl } from './RadiusControl';

const REALTY_TYPE_OPTIONS = [
  { title: 'Apartment', value: 0 },
  { title: 'Villa', value: 1 },
  { title: 'Land', value: 2 },
];

const COOLDOWN_OPTIONS = [
  { label: '10 min', value: 600 },
  { label: '30 min', value: 1800 },
  { label: '1 h', value: 3600 },
  { label: '6 h', value: 21600 },
  { label: '12 h', value: 43200 },
  { label: '24 h', value: 86400 },
];

const defaultFilters = {
  cities: [],
  sources: [],
  price_min: null,
  price_max: null,
  types: [],
  hide_imported: true,
  name_pattern: '',
};

const toOptions = values => values.map(value => ({ title: value, value }));

const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const matchesGlob = (subject, pattern) => {
  if (!pattern || pattern === '*') return true;
  let regex = '';
  let escaping = false;
  [...pattern].forEach(char => {
    if (escaping) {
      regex += escapeRegex(char);
      escaping = false;
      return;
    }
    if (char === '\\') {
      escaping = true;
      return;
    }
    if (char === '*') regex += '.*';
    else if (char === '?') regex += '.';
    else regex += escapeRegex(char);
  });
  if (escaping) regex += '\\\\';
  return new RegExp(`^${regex}$`, 'iu').test(subject || '');
};

export const AutoImportRuleModal = ({
  isOpen,
  onClose,
  token,
  initialRule = null,
  initialRadius = 25,
  initialFilters = {},
  onSaved,
}) => {
  const mergedFilters = {
    ...defaultFilters,
    ...(initialRule?.filters || initialFilters),
  };
  const [name, setName] = useState(initialRule?.name || '');
  const [enabled, setEnabled] = useState(initialRule?.enabled ?? true);
  const [cooldownSeconds, setCooldownSeconds] = useState(
    initialRule?.cooldown_seconds || 600
  );
  const [maxPerRun, setMaxPerRun] = useState(
    initialRule?.max_per_run !== null && initialRule?.max_per_run !== undefined
      ? String(initialRule.max_per_run)
      : ''
  );
  const [radiusKm, setRadiusKm] = useState(
    initialRule?.filters?.radius_km || initialRadius
  );
  const [filters, setFilters] = useState(mergedFilters);
  const [notes, setNotes] = useState(initialRule?.notes || '');
  const debouncedRadius = useDebounce(radiusKm, 500);
  const debouncedFilters = useDebounce(filters, 500);
  const [createRule, createState] = useCreateImportRuleMutation();
  const [updateRule, updateState] = useUpdateImportRuleMutation();
  const { data: spidersData } = useGetSpidersQuery(
    { token },
    { skip: !token || !isOpen }
  );
  const savedPreview = useGetImportRulePreviewCountQuery(
    { token, ruleId: initialRule?.id },
    { skip: !token || !isOpen || !initialRule?.id }
  );
  const createPreview = useGetRecordsQuery(
    {
      token,
      radius_km: debouncedRadius,
      filters: debouncedFilters,
      page: 1,
      per_page: 1000,
      sort: 'distance',
    },
    { skip: !token || !isOpen || !!initialRule?.id }
  );
  const spiderOptions = useMemo(
    () =>
      (spidersData?.data || []).map(source => ({
        title: source,
        value: source,
      })),
    [spidersData]
  );
  const selectedSources = useMemo(
    () => toOptions(filters.sources || []),
    [filters.sources]
  );
  const selectedTypes = useMemo(
    () =>
      (filters.types || [])
        .map(value =>
          REALTY_TYPE_OPTIONS.find(option => option.value === value)
        )
        .filter(Boolean),
    [filters.types]
  );
  const createModePreviewCount = useMemo(() => {
    const rows = createPreview.data?.data ?? [];
    return rows.filter(record =>
      matchesGlob(record.title || '', debouncedFilters.name_pattern || '')
    ).length;
  }, [createPreview.data, debouncedFilters.name_pattern]);
  const previewCount = initialRule?.id
    ? savedPreview.data?.matched_count
    : createModePreviewCount;
  const isDebouncePending =
    radiusKm !== debouncedRadius || filters !== debouncedFilters;
  const isPreviewLoading = initialRule?.id
    ? savedPreview.isFetching
    : createPreview.isFetching || isDebouncePending;
  const isSaving = createState.isLoading || updateState.isLoading;
  const setFilter = patch => setFilters(current => ({ ...current, ...patch }));

  const handleSave = async () => {
    const trimmedMax = maxPerRun.trim();
    const body = {
      name: name.trim(),
      enabled,
      cooldown_seconds: Number(cooldownSeconds),
      max_per_run: trimmedMax === '' ? null : Number(trimmedMax),
      filters: { ...filters, radius_km: Number(radiusKm) },
      notes: notes.trim() || null,
    };
    try {
      if (initialRule?.id)
        await updateRule({ token, ruleId: initialRule.id, body }).unwrap();
      else await createRule({ token, body }).unwrap();
      toast.success(
        initialRule?.id
          ? 'Auto-import rule updated'
          : 'Auto-import rule created'
      );
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error?.data?.message || 'Could not save auto-import rule');
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      classes="w-[95vw] max-w-3xl"
      content={
        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-col gap-1 pr-8">
            <span className="text-xs text-darkGrayColor uppercase tracking-wider">
              Parser auto-import
            </span>
            <h2
              id="modal-title"
              className="text-lg font-semibold text-blackColor"
            >
              {initialRule ? 'Edit rule' : 'New rule'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex flex-col gap-1 md:col-span-3">
              <label className="text-xs text-darkGrayColor">Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor"
                placeholder="e.g. Nice terraces under €3M"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-blackColor cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
                className="accent-purpleColor"
              />
              Enabled
            </label>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-darkGrayColor">Cooldown</label>
              <select
                value={cooldownSeconds}
                onChange={e => setCooldownSeconds(Number(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor bg-white"
              >
                {COOLDOWN_OPTIONS.map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-darkGrayColor">
                Max records per run
              </label>
              <input
                type="number"
                min={1}
                max={5000}
                value={maxPerRun}
                onChange={e => setMaxPerRun(e.target.value)}
                placeholder="Default (200)"
                className="px-3 py-2 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor"
              />
              <span className="text-[11px] text-darkGrayColor">
                Leave empty to use the platform default. Runs matching more than
                this are blocked.
              </span>
            </div>
          </div>
          <div className="p-3 rounded border border-gray-200 bg-lightestGrayColor">
            <RadiusControl
              value={radiusKm}
              onChange={setRadiusKm}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded border border-gray-200 bg-lightestGrayColor">
            <div className="flex flex-col gap-1 md:col-span-2">
              <label className="text-xs text-darkGrayColor">
                Sources (spiders)
              </label>
              <div className="px-2 border border-gray-200 rounded bg-white">
                <SelectInput
                  placeholder="All sources"
                  options={spiderOptions}
                  initialSelected={selectedSources}
                  isMulti
                  isSearchable
                  onSelect={list =>
                    setFilter({ sources: list.map(option => option.value) })
                  }
                  onRemove={list =>
                    setFilter({ sources: list.map(option => option.value) })
                  }
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-darkGrayColor">Realty type</label>
              <div className="px-2 border border-gray-200 rounded bg-white">
                <SelectInput
                  placeholder="All types"
                  options={REALTY_TYPE_OPTIONS}
                  initialSelected={selectedTypes}
                  isMulti
                  onSelect={list =>
                    setFilter({ types: list.map(option => option.value) })
                  }
                  onRemove={list =>
                    setFilter({ types: list.map(option => option.value) })
                  }
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-darkGrayColor">
                Min price (€)
              </label>
              <input
                type="number"
                value={filters.price_min ?? ''}
                onChange={e =>
                  setFilter({
                    price_min:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                className="px-2 py-1 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-darkGrayColor">
                Max price (€)
              </label>
              <input
                type="number"
                value={filters.price_max ?? ''}
                onChange={e =>
                  setFilter({
                    price_max:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                className="px-2 py-1 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-blackColor cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!filters.hide_imported}
                  onChange={e => setFilter({ hide_imported: e.target.checked })}
                  className="accent-purpleColor"
                />
                Hide already-imported
              </label>
            </div>
            <div className="flex flex-col gap-1 md:col-span-3">
              <label className="text-xs text-darkGrayColor">Name pattern</label>
              <input
                type="text"
                value={filters.name_pattern || ''}
                onChange={e => setFilter({ name_pattern: e.target.value })}
                placeholder="*terrasse*"
                className="px-2 py-1 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor"
              />
              <span className="text-[11px] text-darkGrayColor">
                Use * to match anything, ? for one char. Example: *terrasse*
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-darkGrayColor">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="px-3 py-2 text-sm border border-gray-200 rounded outline-none focus:border-purpleColor resize-none"
              placeholder="Optional notes for this rule"
            />
          </div>
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-200">
            <span className="text-xs text-darkGrayColor">
              {isPreviewLoading
                ? 'Checking current matches…'
                : `This rule would match ~${Number(previewCount ?? 0).toLocaleString('fr-FR')} records right now`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-darkGrayColor rounded border border-gray-200 hover:bg-lightGrayColor"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || name.trim() === ''}
                className="px-3 py-1.5 text-sm text-white rounded border border-purpleColor bg-purpleColor disabled:opacity-50"
              >
                {isSaving ? 'Saving…' : 'Save rule'}
              </button>
            </div>
          </div>
        </div>
      }
    />
  );
};
